import { useEffect, useState } from 'react';

import type { Id, QltSlotAttendance, QltViolation } from '../../api/types';
import { Modal } from '../../components';
import { REASON_PRESETS } from '../../config/violation-reasons';
import { classLabel } from '../periodLabels';
import { toggleItem } from './checklistText';
import { CONDUCT_RULES, EMPTY_DRAFTS, clearSaved, draftEntries, type ConductDrafts } from './conductEntry';
import '../../styles/class-management.css';

// the box holds the generated sentence as well as the teacher's words
const TEXT_LIMIT = 400;
const TYPE_NAME: Record<QltViolation['type'], string> = { commendation: 'Khen thưởng', violation: 'Vi phạm kỉ luật' };
const WEEKDAYS = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];

function slotSubtitle(classTitle: string, date: string): string {
  const day = date.slice(0, 10);
  const parts = day.split('-');
  return `${classLabel(classTitle)} - ${WEEKDAYS[new Date(`${day}T00:00:00Z`).getUTCDay()] || ''}, ${parts[2]}/${parts[1]}/${parts[0]}`;
}

export interface ViolationFormValue {
  student: Id;
  type: QltViolation['type'];
  description: string;
}

export interface ViolationFormProps {
  open: boolean;
  classTitle: string;
  date: string;
  initialType: QltViolation['type'];
  students: QltSlotAttendance[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  // resolves with the types that were recorded; a failed one stays in the form for a retry
  onSubmit: (values: ViolationFormValue[]) => Promise<QltViolation['type'][]>;
}

function TypeOption({ tone, active, filled, onSelect }: { tone: QltViolation['type']; active: boolean; filled: boolean; onSelect: () => void }) {
  return (
    <button type="button" role="radio" aria-checked={active} className={`cm-type cm-type--${tone}${active ? ' is-on' : ''}`} onClick={onSelect}>
      <span className="cm-type__box" aria-hidden={true}>✓</span>{TYPE_NAME[tone]}
      {filled ? <span className="cm-type__dot" title="Đã có nội dung" aria-label="đã có nội dung" /> : null}
    </button>
  );
}

export function ViolationForm({ open, classTitle, date, initialType, students, saving, error, onClose, onSubmit }: ViolationFormProps) {
  const [studentId, setStudentId] = useState('');
  const [type, setType] = useState<QltViolation['type']>(initialType);
  const [drafts, setDrafts] = useState<ConductDrafts>(EMPTY_DRAFTS);

  useEffect(() => {
    if (!open) return;
    setStudentId(students.length > 0 ? String(students[0].student._id || students[0].student.id || '') : '');
    setType(initialType);
    setDrafts(EMPTY_DRAFTS);
  }, [open, initialType, students]);

  const entry = drafts[type];
  const entries = draftEntries(drafts);
  const other: QltViolation['type'] = type === 'commendation' ? 'violation' : 'commendation';
  const otherFilled = entries.some((item) => item.type === other);

  function toggleReason(reason: string) {
    setDrafts((current) => ({ ...current, [type]: toggleItem(current[type], CONDUCT_RULES[type], reason) }));
  }

  function editText(text: string) {
    setDrafts((current) => ({ ...current, [type]: { ...current[type], text } }));
  }

  // one record per filled type: a record carries one type, so reward and discipline never share a text
  async function submit() {
    const saved = await onSubmit(entries.map((item) => ({ student: studentId, type: item.type, description: item.description })));
    setDrafts((current) => clearSaved(current, saved));
    const left = entries.filter((item) => saved.indexOf(item.type) < 0);
    if (left.length > 0) setType(left[0].type);
  }

  const contentLabel = type === 'commendation' ? 'Nội dung khen thưởng' : 'Nội dung Vi phạm';
  const actions = <button type="button" className="cm-btn-primary" disabled={saving || !studentId || entries.length === 0} onClick={() => void submit()}>{saving ? 'Đang lưu…' : entries.length > 1 ? `Xác nhận ghi sổ (${entries.length})` : 'Xác nhận ghi sổ'}</button>;

  return (
    <Modal open={open} title="Khen thưởng - Kỉ luật" description={slotSubtitle(classTitle, date)} className="cm-modal" actions={actions} onClose={onClose}>
      <label className="cm-label" htmlFor="violation-student">Học sinh</label>
      <select id="violation-student" className="cm-select" value={studentId} onChange={(event) => setStudentId(event.target.value)}>
        {students.length === 0 ? <option value="">Chưa có danh sách học sinh</option> : students.map((row) => <option key={row.student._id} value={String(row.student._id || row.student.id)}>{row.student.name || row.student.code || 'Học sinh'}</option>)}
      </select>
      <div className="cm-type-toggle" role="radiogroup" aria-label="Loại ghi nhận">
        <TypeOption tone="commendation" active={type === 'commendation'} filled={type !== 'commendation' && otherFilled} onSelect={() => setType('commendation')} />
        <TypeOption tone="violation" active={type === 'violation'} filled={type !== 'violation' && otherFilled} onSelect={() => setType('violation')} />
      </div>
      <div className="cm-label">{contentLabel}</div>
      <div className="cm-checklist" role="group" aria-label={contentLabel}>
        {REASON_PRESETS[type].map((reason) => (
          <label className={entry.ticks.indexOf(reason) >= 0 ? 'cm-check is-on' : 'cm-check'} key={reason}>
            <input type="checkbox" checked={entry.ticks.indexOf(reason) >= 0} onChange={() => toggleReason(reason)} />
            <span>{reason}</span>
          </label>
        ))}
      </div>
      <label className="cm-label" htmlFor="violation-text">Nội dung ghi sổ</label>
      <div className="cm-note">
        <textarea id="violation-text" value={entry.text} maxLength={TEXT_LIMIT} onChange={(event) => editText(event.target.value)} placeholder="Chọn nội dung ở trên hoặc nhập nội dung..." />
        <span className="cm-note__count">{entry.text.length}/{TEXT_LIMIT}</span>
      </div>
      {otherFilled ? <p className="cm-hint">Phần {TYPE_NAME[other]} đã có nội dung, sẽ được ghi sổ thành một mục riêng.</p> : null}
      {error ? <p className="cm-error" role="alert">{error}</p> : null}
    </Modal>
  );
}
